import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tag, ArrowLeft, ArrowRight, X } from "lucide-react";

interface EditDetailsProps {
  kbData: any;
  onUpdate: (updates: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const EditDetails = ({ kbData, onUpdate, onNext, onBack }: EditDetailsProps) => {
  const [keyword, setKeyword] = useState(kbData.keyword || "");
  const [category, setCategory] = useState(kbData.category || "");
  const [tags, setTags] = useState<string[]>(kbData.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [notes, setNotes] = useState(kbData.notes || "");

  useEffect(() => {
    setKeyword(kbData.keyword || "");
    setCategory(kbData.category || "");
    setTags(kbData.tags || []);
    setNotes(kbData.notes || "");
  }, [kbData]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleNext = () => {
    onUpdate({
      keyword,
      category,
      tags,
      notes,
    });
    onNext();
  };

  return (
    <Card className="border-2 border-black">
      <CardHeader className="bg-slate-100 border-b border-black">
        <CardTitle className="font-serif flex items-center">
          <Tag className="mr-2" size={24} />
          Additional Details
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="keyword" className="text-base font-semibold">
                Primary Keyword
              </Label>
              <Input
                id="keyword"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="e.g., AI, Construction, etc."
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="category" className="text-base font-semibold">
                Category
              </Label>
              <Input
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., Research, Tutorial, etc."
                className="mt-2"
              />
            </div>
          </div>

          <div>
            <Label className="text-base font-semibold">Tags</Label>
            <p className="text-sm text-gray-500 mb-2">
              Add tags to help categorize and search for this knowledge base
            </p>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Add a tag and press Enter"
              />
              <Button
                type="button"
                onClick={handleAddTag}
                variant="outline"
                className="border-2 border-black"
              >
                Add
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {tags.map((tag, index) => (
                  <Badge key={index} variant="outline" className="border-black flex items-center gap-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:text-red-600"
                    >
                      <X size={14} />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="notes" className="text-base font-semibold">
              Notes
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes or context"
              rows={4}
              className="mt-2"
            />
          </div>

          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="border-2 border-black"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              className="bg-black hover:bg-black/90"
            >
              Next: Documents
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
